// =============================================================================
// ADMIN — EXHAUSTIVE i18n KEY MAPS for the partner ecosystem (Step-0 PR-0C-1)
// =============================================================================
// The underlying fields are typed `string` in the frontend, so their real
// domains are the backend DB CHECK constraints (migration 496 + 518) and the
// frontend const arrays. Each map is typed Record<Union,string> so the compiler
// enforces exhaustiveness against these local unions; tEnum adds the runtime
// guard for a value off the DB contract (dev-throw / prod-marker). Callers cast
// the string field to the union — the cast is honest because tEnum backstops it.
// No new keys: every value already resolves in admin/en.json.
// =============================================================================

// DB CHECK partner_program_invitations.status  (invitation lifecycle)
export type InvStatus = 'sent' | 'completed' | 'confirmed' | 'declined' | 'expired';
// DB CHECK claimed_scope (migration 496:172)
export type Scope = 'national' | 'local' | 'lea';
// DB CHECK claim_state (496:175)
export type ClaimState = 'unvetted' | 'under_review' | 'vetted' | 'declined';
// DB CHECK vetting_actor_type (496:109)
export type ActorType = 'agenticed_staff' | 'city_marketplace' | 'district' | 'none';
// DB CHECK activation_source (496:284 + 518 contract_purchase)
export type ActivationSource =
  | 'marketplace_browse' | 'direct_add' | 'invitation_completed'
  | 'rfp_award' | 'workflow_referral' | 'contract_purchase';
// DECLINE_REASONS const (PartnerActionDrawers.tsx)
export type DeclineReason =
  | 'already_have_provider' | 'services_mismatch' | 'unable_to_verify' | 'scope_mismatch' | 'other';
// ORG_TYPES const (PartnerActionDrawers.tsx)
export type OrgType = 'nonprofit' | 'government' | 'school_based' | 'faith_based' | 'for_profit' | 'other';
// SERVICE_CATEGORIES / lensMappings categories
export type PartnerCategory =
  | 'academic_support' | 'college_career' | 'enrichment' | 'mentoring' | 'family_support'
  | 'health_wellness' | 'youth_development' | 'special_populations' | 'food_nutrition'
  | 'housing' | 'legal_aid' | 'financial' | 'workforce';
// Lens ids (config/lensMappings) — hyphenated; keys use the underscored form.
export type LensId = 'family-support' | 'student-programs' | 'workforce';

export const INV_STATUS_KEY: Record<InvStatus, string> = {
  sent: 'partner_applications_inv_status_sent', completed: 'partner_applications_inv_status_completed',
  confirmed: 'partner_applications_inv_status_confirmed', declined: 'partner_applications_inv_status_declined',
  expired: 'partner_applications_inv_status_expired',
};
export const APPLICATIONS_SCOPE_KEY: Record<Scope, string> = {
  national: 'partner_applications_scope_national', local: 'partner_applications_scope_local',
  lea: 'partner_applications_scope_lea',
};
export const CLAIM_STATE_KEY: Record<ClaimState, string> = {
  unvetted: 'partner_applications_claim_state_unvetted', under_review: 'partner_applications_claim_state_under_review',
  vetted: 'partner_applications_claim_state_vetted', declined: 'partner_applications_claim_state_declined',
};
export const ACTOR_KEY: Record<ActorType, string> = {
  agenticed_staff: 'partner_applications_actor_agenticed_staff', city_marketplace: 'partner_applications_actor_city_marketplace',
  district: 'partner_applications_actor_district', none: 'partner_applications_actor_none',
};
export const ACTIVATION_SOURCE_KEY: Record<ActivationSource, string> = {
  marketplace_browse: 'partner_applications_source_marketplace_browse', direct_add: 'partner_applications_source_direct_add',
  invitation_completed: 'partner_applications_source_invitation_completed', rfp_award: 'partner_applications_source_rfp_award',
  workflow_referral: 'partner_applications_source_workflow_referral', contract_purchase: 'partner_applications_source_contract_purchase',
};
export const APPLICATIONS_DECLINE_REASON_KEY: Record<DeclineReason, string> = {
  already_have_provider: 'partner_applications_decline_reason_already_have_provider',
  services_mismatch: 'partner_applications_decline_reason_services_mismatch',
  unable_to_verify: 'partner_applications_decline_reason_unable_to_verify',
  scope_mismatch: 'partner_applications_decline_reason_scope_mismatch',
  other: 'partner_applications_decline_reason_other',
};
export const PARTNER_CATEGORY_KEY: Record<PartnerCategory, string> = {
  academic_support: 'partner_category_academic_support', college_career: 'partner_category_college_career',
  enrichment: 'partner_category_enrichment', mentoring: 'partner_category_mentoring',
  family_support: 'partner_category_family_support', health_wellness: 'partner_category_health_wellness',
  youth_development: 'partner_category_youth_development', special_populations: 'partner_category_special_populations',
  food_nutrition: 'partner_category_food_nutrition', housing: 'partner_category_housing',
  legal_aid: 'partner_category_legal_aid', financial: 'partner_category_financial', workforce: 'partner_category_workforce',
};
export const LENS_LABEL_KEY: Record<LensId, string> = {
  'family-support': 'partner_network_lens_family_support',
  'student-programs': 'partner_network_lens_student_programs',
  workforce: 'partner_network_lens_workforce',
};
export const LENS_DESC_KEY: Record<LensId, string> = {
  'family-support': 'partner_network_lens_family_support_desc',
  'student-programs': 'partner_network_lens_student_programs_desc',
  workforce: 'partner_network_lens_workforce_desc',
};
export const NETWORK_SCOPE_KEY: Record<Scope, string> = {
  national: 'partner_network_scope_national', local: 'partner_network_scope_local', lea: 'partner_network_scope_lea',
};
export const ORG_TYPE_KEY: Record<OrgType, string> = {
  nonprofit: 'partner_network_org_type_nonprofit', government: 'partner_network_org_type_government',
  school_based: 'partner_network_org_type_school_based', faith_based: 'partner_network_org_type_faith_based',
  for_profit: 'partner_network_org_type_for_profit', other: 'partner_network_org_type_other',
};
export const NETWORK_DECLINE_REASON_KEY: Record<DeclineReason, string> = {
  already_have_provider: 'partner_network_decline_reason_already_have_provider',
  services_mismatch: 'partner_network_decline_reason_services_mismatch',
  unable_to_verify: 'partner_network_decline_reason_unable_to_verify',
  scope_mismatch: 'partner_network_decline_reason_scope_mismatch',
  other: 'partner_network_decline_reason_other',
};
